<!-- pages/TourDetailPage.vue -->
<template>
  <div class="tour-detail-page">
    <TourDetailHeader :tour="tour" />

    <TourGallery id="tag-gallery" :images="tour.gallery" />

    <div class="tour-detail-content container">
      <div class="tour-detail-top">
        <div class="left-content">
          <TourDetailHeading :tour="tour" />
          <TourAwards :tour="tour" />
          <TourBrandCard :tour="tour" />
          <TourHighlight :highlights="tour.highlights" />

          <TourDetailHero :video="tour.video" :tour-banner="tour.tourBanner" />

          <TourDescription class="section fold-section" id="tag-description" :tour="tour" />
          <SitesVisitedIncluded class="section fold-section" id="tag-site-visited" :tour="tour" />
          <ReviewsRating id="tag-reviews" class="section reviews" />
          <ImportantInfo class="section fold-section section-important" :info="tour.tourImportantInfo" />
          <TourPlan
            class="perfect-day section"
            :expanded="screenWidth > 768"
            :perfect-day="tour.perfectDay"
          />
        </div>

        <client-only>
          <div class="right-content">
            <div class="right-content-close">
              <img
                :data-src="
                  require('~/assets/svg/close-booking-widget-anchor.svg')
                "
                class="lazyload right-content-close-icon"
                 @click="closeBookPanel"
              />
            </div>
            <BookingWidget
              v-if="tour.ventrataProduct && tourAvailable"
              :product="tour.ventrataProduct"
              :promo="tour.promo"
              @updatePrice="onUpdatePrice"
              @updateDiscountPrice="onUpdateDiscountPrice"
            />
            <div v-else>
              <BookingUnavailable
                :tours="similarTours"
              />
            </div>
          </div>
        </client-only>

        <div class="mobile-bottom-panel">
          <div v-if="showPromoTag" class="promo-tag" :style="getPromoStyle">
            {{ getPromoLabel() }}
          </div>
          <div class="booking-widget-content">
            <span class="price-value-footer">
              <tempalte v-if="price">
                <span class="price-from">
                  From
                </span>
                <span class="dollar-prefix">
                  {{ getPrefixSymbol() }}
                </span>
                <span :class="{ strikethrough: discountPrice }" class="dollar-price">{{ price }}</span>
                <span v-if="discountPrice" class="discounted-price dollar-price">{{ discountPrice }}</span>
                <div class="free-cancellation">
                  <svgicon
                    class="icon icon-checkmark"
                    height="23"
                    name="checkmark_circled"
                    width="23"
                  />Free Cancellation
                </div>
              </tempalte>
            </span>
            <div id="book-footer-btn-container">
              <b-btn
                v-b-toggle.accordion3
                class="book-footer-btn btn-anim gold-dark"
                @click="clickMobileBook()"
              >
                Book Now
              </b-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
    <client-only>
      <SimilarTours class="container container-similar-tours section" :tours="this.tourTransformer" />
    </client-only>
  </div>
</template>

<script>
import Vue from 'vue'
import * as BootstrapVue from 'bootstrap-vue'
Vue.use(BootstrapVue)
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'

import $ from "jquery";
import { mapGetters } from "vuex";
import { uaMatch } from "~/helpers/client.js";

// Imported Components
import ReviewsRating from "~/components/utils/ReviewsRating";
import BookingWidget from '@/components/containers/tour/BookingWidget';
import BookingUnavailable from '@/components/containers/tour/BookingUnavailable';
import TourDetailHeader from "~/components/containers/tour/TourDetailHeader";
import TourDetailHero from "~/components/containers/tour/TourDetailHero";
import TourDetailHeading from "~/components/containers/tour/TourDetailHeading";
import TourAwards from "~/components/containers/tour/TourAwards";
import TourBrandCard from "~/components/containers/tour/TourBrandCard";
import TourGallery from "~/components/containers/tour/TourGallery";
import FoldSection from "~/components/containers/FoldSection";
import TourPlan from "~/components/containers/tour/TourPlan";
import TourHighlight from "@/components/containers/tour/TourHighlight.vue";
import TourDescription from "@/components/containers/tour/TourDescription";
import SitesVisitedIncluded from "@/components/containers/tour/SitesVisitedIncluded";
import ImportantInfo from "@/components/containers/tour/ImportantInfo";
import SimilarTours from "@/components/containers/tour/SimilarTours";

// Imported Images
import imageOptimo from "~/mixins/imageOptimoMixin.js";
import setRobots from "~/mixins/setRobots.js";
import promoMixin from "~/mixins/promoMixin.js";

export default {
  name: "TourDetailPage",
  mixins: [imageOptimo, setRobots, promoMixin],
  components: {
    BookingWidget,
    BookingUnavailable,
    SimilarTours,
    SitesVisitedIncluded,
    ImportantInfo,
    TourDescription,
    TourHighlight,
    TourPlan,
    TourDetailHeader,
    TourDetailHero,
    TourDetailHeading,
    TourAwards,
    TourBrandCard,
    ReviewsRating,
    TourGallery,
    FoldSection
  },
  async validate({ store, params }) {
    const cityUrl = params.city?.trim();
    const tourUrl = params.tour?.trim();

    const tourReq = store.dispatch("getTour", {
      city: cityUrl,
      tour: tourUrl
    });

    const reviewReq = store.dispatch("getTourReviews", {
      offset: 0,
      limit: 10,
      slug: tourUrl
    });

    try {
      const [tour] = await Promise.all([tourReq, reviewReq]);
      const { citySlugs, citySlug } = tour;
      if (citySlug !== cityUrl && !citySlugs.includes(cityUrl)) {
        // WLK-6394: xx-tours/some-valid-tour  shouldn't be valid
        return false;
      }
    } catch (err) {
      return false;
    }
    return true;
  },
  async fetch({ store, route, params, redirect, req }) {
    const cityUrl = params.city?.trim();
    const tour = store.state.TourList.tour;
    const { citySlug } = tour;
    if (params.tour.endsWith(" ") || citySlug !== cityUrl) {
      redirect(`/${citySlug}/${params.tour.trim()}/`);
    }
    var currentPath = [
      "https://www.walksofitaly.com",
      "https://www.walksofitaly.com/" + cityUrl,
      "https://www.walksofitaly.com" + route.path?.trim()
    ];
    store.dispatch("getBreadCrumbs", { urls: currentPath });

    store.commit("setUA", uaMatch(req?.headers["user-agent"] ?? ""));
  },
  head() {
    const blacklist = ['paris-tours', 'bordeaux-tours']
    const robots = blacklist.includes(this.$route.params.city) ? 'noindex' : this.setRobotsTag()

    return {
      title: this.tour.metaTitle,
      link: [
        {
          rel: "canonical",
          href: this.getMetaURL()
        }
      ],
      meta: [
        {
          hid: "robots",
          name: "robots",
          content: robots
        },
        {
          hid: "title",
          name: "title",
          content: this.tour.metaTitle
        },
        {
          hid: "description",
          name: "description",
          content: this.getDescPrefix() + this.tour.metaDescription
        },
        {
          hid: "og:title",
          name: "og:title",
          property: "og:title",
          content: this.tour.metaTitle
        },
        {
          hid: "og:description",
          name: "og:description",
          property: "og:description",
          content: this.tour.metaDescription
        },
        {
          hid: "og:image",
          name: "og:image",
          property: "og:image",
          content: this.tour.videoThumbnail
        },
        {
          hid: "og:type",
          name: "og:type",
          property: "og:type",
          content: "article"
        },
        {
          hid: "og:url",
          name: "og:url",
          property: "og:url",
          content: this.getMetaURL()
        },
        {
          hid: "fb:app_id",
          name: "fb:app_id",
          content: 1989124628036695
        },
        {
          property: "twitter:card",
          content: "summary_large_image"
        },
        {
          property: "twitter:site",
          content: "@walks"
        },
        {
          property: "twitter:title",
          content: this.tour.metaTitle
        },
        {
          property: "twitter:card",
          content: "summary_large_image"
        },
        {
          property: "twitter:description",
          content: this.tour.metaDescription
        },
        {
          property: "twitter:image",
          content: "https:" + this.tour.videoThumbnail
        }
      ],
      script: [
        {
          hid: "ldjson-schema",
          innerHTML: this.tour.microData,
          type: "application/ld+json"
        },
        {
          hid: "ldjson-schema",
          innerHTML: this.getBreadCrumbs(),
          type: "application/ld+json"
        }
      ],
      __dangerouslyDisableSanitizersByTagID: {
        "ldjson-schema": ["innerHTML"]
      }
    };
  },
  data () {
    return {
      tourAvailable: true,
      price: '',
      discountPrice: ''
    }
  },
  computed: {
    ...mapGetters([
      "tour",
      "breadCrumbs",
      "reviews",
      "reviewData",
      "tourMeetingPoint",
      "screenWidth",
    ]),
    similarTours () {
      return (this.tour.similarTours ? this.tour.similarTours : []).map(tour => {
        return {
          citySlug: tour.citySlug,
          propertyId: tour.propertyId,
          bookingTypeId: tour.bookingTypeId,
          shortTitle: tour.title,
          tourPageUrl: tour.tourPageUrl,
          listingImage: tour.featuredImage,
          listingImageTitle: tour.listingImageTitle,
          reviewStatus: tour.reviewStatus,
          ventrataCode: tour.ventrataCode,
          ventrataProductId: tour.ventrataProductId,
          priceMap: tour.priceMap,
        }
      })
    },
    tourTransformer() {
      return this.tour.similarTours.map(tour => {
        let reviewStats = tour.reviewStatus;
        if (!reviewStats) {
          // default value
          reviewStats = {
            feedbackAverage: 0,
            feedbackCount: 0
          };
        }
        return {
          citySlug: tour.citySlug,
          priceMap: tour.priceMap,
          reviewStats,
          tourId: tour.eventId,
          tourListingImage: tour.featuredImage,
          tourPageURL: tour.slug,
          tourPageTitleShort: tour.name,
          bookingTypeId: tour.bookingTypeId,
          propertyId: tour.propertyId
        };
      });
    },
    showPromoTag() {
      if (
        this.tour.promo &&
        !this.unavailabile &&
        this.tour.promo.show &&
        this.tour.promo.promoLocation.includes("Product Page")
      ) {
        return true;
      } else {
        return false;
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (process.browser) {
        this.scrollToSpy();

        window.onpopstate = event => {
          // Handle the "go back" event here
          // console.log('User clicked on the browser back button');
          $("#app").removeClass("freeze-scroll");
        };
      }
    });
    this.gtmInit();

    // client-side update review
    const tourUrl = this.$route.params.tour;
    this.$store.dispatch("getTourReviews", {
      slug: tourUrl,
      offset: 0,
      limit: 10
    });
    this.$store.dispatch("getTourPageReviewStat", { slug: tourUrl });
    this.$store.dispatch("addRecentlyViewed", this.tour);
  },
  methods: {
    onUpdatePrice(price) {
      this.price = price
    },
    onUpdateDiscountPrice(price) {
      this.discountPrice = price
    },
    getDescPrefix() {
      const path = this.$router.currentRoute.path;
      if (path && this.tour.tourSlug == "highlights-vatican-museums-tour") {
        if (path.indexOf("vatican-tours") > -1) {
          return "Vatican ";
        }
        if (path.indexOf("rome-tours") > -1) {
          return "Rome ";
        }
      }
      return "";
    },

    gtmInit() {
      let impressionsTours = [];
      this.tourTransformer.forEach((tour, i) => {
        impressionsTours.push({
          name: tour.tourPageTitleShort,
          id: tour.bookingTypeId,
          price: this.getAnchorPriceNum(tour),
          category:
            tour.citySlug
              ?.replace("-tours", "")
              .charAt(0)
              .toUpperCase() + tour.citySlug?.replace("-tours", "").slice(1),
          list: "Related Products Widget",
          position: i + 1
        });
      });
      window.dataLayer.push({
        ecommerce: {
          currencyCode: "USD",
          impressions: impressionsTours
        }
      });
      this.gtmPageView({
        hostname: "www.walksofitaly.com",
        fullURL:
          "www.walksofitaly.com" +
          this.$route.path.substring(0, this.$route.path.length - 1),
        pagePath: this.$route.path.substring(0, this.$route.path.length - 1),
        pageTitle: 'Product Page',
        referrer: document.referrer
      });
      window.dataLayer.push({
        event: "domReady",
        domReady: {
          hostname: "www.walksofitaly.com",
          fullURL:
            "www.walksofitaly.com" +
            this.$route.path.substring(0, this.$route.path.length - 1),
          pagePath: this.$route.path.substring(0, this.$route.path.length - 1),
          pageTitle: 'Product Page',
        }
      });
      let region = "";
      if (this.tour.citySlug?.replace("-tours", "").toLowerCase() === "rome") {
        region = "Lazio";
      } else if (
        this.tour.citySlug?.replace("-tours", "").toLowerCase() === "florence"
      ) {
        region = "Toscana";
      } else if (
        this.tour.citySlug?.replace("-tours", "").toLowerCase() === "venice"
      ) {
        region = "Veneto";
      } else if (
        this.tour.citySlug?.replace("-tours", "").toLowerCase() === "milan"
      ) {
        region = "Lombardia";
      } else if (
        this.tour.citySlug?.replace("-tours", "").toLowerCase() === "pompeii"
      ) {
        region = "Campania";
      } else {
        region = "Rest of Italy";
      }
      window.dataLayer.push({
        event: "productDetailView",
        ecommerce: {
          detail: {
            actionField: { list: "Product Pages" },
            products: [
              {
                name: this.tour.shortTitle,
                id: this.tour.bookingTypeId,
                price: this.getAnchorPriceNum(this.tour),
                category:
                  this.tour.citySlug
                    ?.replace("-tours", "")
                    .charAt(0)
                    .toUpperCase() +
                  this.tour.citySlug?.replace("-tours", "").slice(1),
                dimension3:
                  this.tour.citySlug
                    ?.replace("-tours", "")
                    .charAt(0)
                    .toUpperCase() +
                  this.tour.citySlug?.replace("-tours", "").slice(1),
                dimension4: region,
                dimension5: "Italy",
                metric5: this.timeStringToFloat(
                  this.tour.duration.match(/[0-9]+/g).join(":")
                )
              }
            ]
          }
        }
      });

      this.utagProductDetailsView(this.tour)
    },
    getPriceReviews(value) {
      return this.getPrice(value);
    },
    getBreadCrumbs() {
      return this.breadCrumbs;
    },
    getReviewPath() {
      let leadingSlash = "";
      if (!this.$route.path.match(/\/$/)) {
        leadingSlash = "/";
      }
      return this.$route.path + leadingSlash + "reviews/";
    },
    getImageURL(url) {
      return this.setAutoWidth(url);
    },
    getMetaURL() {
      const curCity = this.tour.citySlug;
      const tourSlug = this.$route.params.tour?.trim();
      // vatican tours should all has vatican-tours canonical
      return `https://www.walksofitaly.com/${curCity}/${tourSlug}/`;
    },
    getSimilarToursClass() {
      if (this.tour.tourGuides && this.tour.tourGuides.length > 0) {
        return "tour-list-section";
      } else {
        return "tour-list-section grey bordered";
      }
    },
    scrollToSpy() {
      // detail scrollToSpy
      let lastId;
      let topMenu = $(".city-tour-tabs, .city-nav-tabs:not(.my-account-tabs)");
      let topMenuHeight = topMenu.outerHeight() + 80;
      // All list items
      let menuItems = topMenu.find("a");
      // Anchors corresponding to menu items
      let scrollItems = menuItems.map(function() {
        let item = $($(this).attr("href"));
        if (item.length) {
          return item;
        }
      });
      // Bind to scroll
      $(window).scroll(function() {
        // Get container scroll position
        let fromTop = $(this).scrollTop() + topMenuHeight;
        // Get id of current scroll item
        let cur = scrollItems.map(function() {
          if ($(this).offset().top < fromTop) return this;
        });
        // Get the id of the current element
        cur = cur[cur.length - 1];
        let id = cur && cur.length ? cur[0].id : "";

        if (lastId !== id) {
          lastId = id;
          // Set/remove active class
          menuItems
            .removeClass("active")
            .filter("[href='#" + id + "']")
            .addClass("active");
        }
      });
    },
    timeStringToFloat(time) {
      var hoursMinutes = time.split(/[.:]/);
      var hours = parseInt(hoursMinutes[0], 10);
      var minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
      return hours + minutes / 60;
    },
    clickMobileBook() {
      $("#app").addClass("freeze-scroll");
      $(".right-content").addClass("animate__animated");
      $(".right-content").addClass("animate__bounceInUp");

      let region = "";
      if (this.tour.cityName.toLowerCase() === "rome") {
        region = "Lazio";
      } else if (this.tour.cityName.toLowerCase() === "florence") {
        region = "Toscana";
      } else if (this.tour.cityName.toLowerCase() === "venice") {
        region = "Veneto";
      } else if (this.tour.cityName.toLowerCase() === "milan") {
        region = "Lombardia";
      } else if (this.tour.cityName.toLowerCase() === "pompeii") {
        region = "Campania";
      } else {
        region = "Rest of Italy";
      }
      window.dataLayer.push({
        event: "clickMobileBookNow",
        click: {
          clickText: "Mobile - Book Now"
        },
        products: [
          {
            name: this.tour.shortTitle,
            id: this.tour.bookingTypeId,
            price: this.getAnchorPriceNum(this.tour),
            category: this.tour.cityName,
            dimension3: this.tour.cityName,
            dimension4: region,
            dimension5: "Italy",
            metric5: this.timeStringToFloat(
              this.tour.duration.match(/[0-9]+/g).join(":")
            )
          }
        ]
      });
    },
    closeBookPanel() {
      $("#app").removeClass("freeze-scroll");
      $(".right-content").removeClass("animate__animated");
      $(".right-content").removeClass("animate__bounceInUp");
    },
    getPromoStyle () {
      if (this.tour.promo) {
        return `
          background-color: ${this.tour.promo.promoTag.bgColor};
          color: ${this.tour.promo.promoTag.textColor};
        `
      } else {
        return ''
      }
    },
    getPromoLabel () {
      if (this.tour.promo && this.tour.promo.promoTag) {
        return this.tour.promo.promoTag.salesText
      } else {
        return ''
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tour-detail-page {
  .tour-detail-content {
    .tour-detail-top {
      overflow: visible;
    }
  }
  .golden-text {
    color: #ad9c63;
  }
  .promo-banner {
    background: #5e2013;
    color: white;
    padding: 0.75em 0;
    text-align: center;
    font-size: 14px;

    .upper-case {
      font-weight: 800;
    }

    .lower-case {
      font-weight: 200;
    }
  }

  .book-footer-btn {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 8px !important;
    width: 120px;
    height: 40px;
    color: #fff;
    background-color: #a09343;
    z-index: 999;
    border: none !important;
    border-radius: 0 !important;
    font-family: "DM Sans";
    font-style: normal;
    font-weight: 700;
    font-size: 14px !important;
    line-height: 16px;

    @include max-width($tablet-s) {
      max-width: 150px;
    }
  }

  .booking-widget-content {
    height: 100px;
    padding: 16px;
    background-color: white;
    border: none;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .price-value-footer {
    flex: 1;
    position: relative;
    @extend %header-5;
    color: $woi-red-main;

    .price-from {
      margin-right: 6px;
    }

    .free-cancellation {
      color: $woi-gold-light;
      margin-top: 16px;
      @media (max-width: 768px) {
        margin-top: 0;
      }
      @extend %subtitle-2;
      .icon-checkmark {
        margin-right: 8px;
      }
    }
  }

  #book-footer-btn-container {
    
  }
}

.section {
  margin-bottom: 3rem;
}

.promo-tag {
  position: absolute;
  top: -44px;
  left: 0;
  width: 100vw;
  padding: 12px;
  text-align: center;
  background-color: #1BB2EB;
  color: #F7F7F7;
  font-weight: 900 !important;
}

@include max-width($mobile) {
  .fold-section {
    margin-bottom: 0;
  }
  .reviews {
    order: 2;
  }
  .section-important {
    order: 1;
  }
  .perfect-day {
    order: 3;
  }
  .right-content {
    padding: 16px 16px 100px;
  }

  .tour-detail-top .right-content .right-content-close {
    height: 60px;
    img {
      filter: brightness(0);
      width: 24px;
      height: 24px;
      margin-right: 16px;
    }
  }
}
</style>

<style lang="scss">
.tour-detail-page {
  #tag-description, #tag-site-visited {
    .section-header {
      .tour-description-heading-detail {
        border-bottom: 1px solid $woi-red-main;
      }
    }
  }

  .dollar-prefix {
    align-self: flex-end;
    font-size: 14px;
    line-height: 36px;
  }

  .dollar-price {
    font-family: Vollkorn;
    font-weight: 700;
    font-size: 24px;
    line-height: 42px;
    letter-spacing: 0px;
    text-align: right;
    color: $blackberry;
  }
  .discounted-price {
    color: $woi-gold-main;
  }
  .strikethrough {
    margin-left: 4px;
    margin-right: 4px;
    text-decoration: line-through;
  }
}
</style>
